import { Extension } from '@tiptap/core';
import type { Node as PmNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { findReferenceMatches, type ReferenceIndex } from './reference-index';

// Decorates known references (inputs.*, outputs.*, task/tool names) with chip styling —
// see styles/prompt-reference.css. Decoration-only: the underlying text stays plain, so
// prompts round-trip through markdown untouched and Percy-authored notation lights up too.
export const createReferenceHighlightExtension = (getReferenceIndex: () => ReferenceIndex) =>
  Extension.create({
    name: 'promptReferenceHighlight',
    addProseMirrorPlugins() {
      const buildDecorations = (doc: PmNode): DecorationSet => {
        const refs = getReferenceIndex();
        const decorations: Decoration[] = [];
        doc.descendants((node, pos) => {
          if (!node.isText || !node.text) return true;
          // Inside code, notation is being quoted/demonstrated — leave the code styling alone.
          if (node.marks.some(mark => mark.type.name === 'code')) return true;
          for (const match of findReferenceMatches(node.text, refs)) {
            decorations.push(
              Decoration.inline(pos + match.start, pos + match.end, {
                class: `prompt-ref prompt-ref-${match.kind}`,
              }),
            );
          }
          return true;
        });
        return DecorationSet.create(doc, decorations);
      };

      return [
        new Plugin({
          key: new PluginKey('promptReferenceHighlight'),
          state: {
            init: (_config, { doc }) => buildDecorations(doc),
            apply: (tr, old) => (tr.docChanged ? buildDecorations(tr.doc) : old),
          },
          props: {
            decorations(state) {
              return this.getState(state);
            },
          },
        }),
      ];
    },
  });
